import { useEffect } from "react";
import { Button } from "~/components/ui/Button";
import type { Preset } from "~/presets";

interface PresetsDrawerProps {
  open: boolean;
  presets: Preset[];
  onOpen: () => void;
  onClose: () => void;
  onSelect: (preset: Preset) => void;
}

export function PresetsDrawer({
  open,
  presets,
  onOpen,
  onClose,
  onSelect,
}: PresetsDrawerProps) {
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <Button onClick={onOpen}>Presets</Button>

      {open ? (
        <>
          <div
            role="presentation"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/60"
          />

          <aside className="scrollbar-slim fixed inset-y-0 left-0 z-50 w-75 overflow-y-auto bg-panel shadow-2xl">
            <h2 className="px-4 py-5 text-center text-xl font-semibold text-neutral-100">
              Presets
            </h2>
            <hr className="border-neutral-700" />

            <ul className="flex flex-col gap-2 p-2">
              {presets.map((preset) => (
                <li key={preset.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(preset);
                      onClose();
                    }}
                    className="w-full cursor-pointer rounded-lg bg-panel-raised p-3 text-left transition-colors hover:bg-neutral-600/40"
                  >
                    <h3 className="text-base font-semibold text-neutral-100">
                      {preset.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-neutral-400">
                      {preset.description}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </>
      ) : null}
    </>
  );
}
